import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ContainerLayout } from 'layouts';
import { FontDetailS, FontBodyL, DigitsInput } from 'components/shared';
import { Interpolate, TranslationContext } from '@ds/react-utils';
import { useButtonsLoading } from 'hooks';
import { apiService } from 'services';
import { routes } from 'static/routes';
import { setFocus } from 'utils/AccessibilityUtils';
import { IPhoneNumber } from 'services/PhoneNumber';
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import { NotificationType } from 'types/notification';
import { AxiosError } from 'axios';

export interface ICheckDigitsVerificationResponseMessages {
    responseResult: string | undefined;
    responseMessage?: string;
    phoneNumbers?: IPhoneNumber[];
}

const PhoneNumberCheckPage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const [inputDigits, setInputDigits] = useState<string>('');
    const history = useHistory();
    const translate = React.useContext(TranslationContext);
    const [textBoxError, setTextBoxError] = useState('');
    const [selectedNotificationType, setSelectedNotificationType] = useState<NotificationType>('SMS');

    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading, areButtonsLoading } = useButtonsLoading();

    const updateInput = (digits: string) => {
        setTextBoxError('');
        setInputDigits(digits);
    };

    const onKeyDown = (key: string) => {
        if (key === 'Enter') {
            sendSolution();
        }
    };

    const sendSolution = async () => {
        if (areButtonsLoading) {
            return;
        }

        if (inputDigits.length !== 4 || inputDigits.match(/[0-9]{4}/)?.length !== 1) {
            setTextBoxError('Code must be 4 numbers');
            setFocusOnDigitsInput();
            return;
        }

        setIsPrimaryButtonLoading(true);

        await apiService.post<any>(
            '/phone/checkDigits',
            {
                digits: inputDigits
            },
            (response: ICheckDigitsVerificationResponseMessages) => {
                handleResponse(response);
            },
            (error: AxiosError) => {
                setIsPrimaryButtonLoading(false);
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                }
            }
        );
    };

    const handleResponse = (response: ICheckDigitsVerificationResponseMessages) => {
        if (response.responseResult === 'PassedCheck' && response.phoneNumbers && response.phoneNumbers[0]) {
            // only sms is permitted at this time
            setSelectedNotificationType('SMS');
            sendCodeAfterPassing(response.phoneNumbers[0]);
        } else if (response.responseResult === 'FailedCheck') {
            if (response.responseMessage === 'PhoneNumberDigitsMismatchError') {
                setTextBoxError('Wrong number, please try again');
                setFocusOnDigitsInput();
                setIsPrimaryButtonLoading(false);
            } else if (response.responseMessage === 'MaxAttemptsExceeded') {
                history.push(routes.DocumentLocked);
            }
        }
    };

    const sendCodeAfterPassing = async (selectedPhoneNumber: IPhoneNumber) => {
        await apiService.post<any>(
            '/phone/send',
            {
                phoneNumber: selectedPhoneNumber.phoneNumber,
                uniqueId: selectedPhoneNumber.uniqueId,
                language: context.language
            },
            () => {
                history.push(routes.ConfirmCode, { selectedPhoneNumber: selectedPhoneNumber, method: selectedNotificationType });
            }
        );
    };

    const setFocusOnDigitsInput = () => {
        setFocus('[data-focus="verify-phone-number-check-last-digits-input"]');
    };

    useEffect(() => {
        setFocusOnDigitsInput();
    }, []);

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title
                    title={translate("To keep you safe, confirm it's really you")}
                    titleDataQa="verify-phone-number-check-title"
                />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <FontBodyL data-qa="verify-phone-number-check-intro">{translate('Enter the last 4 digits of your phone number')}</FontBodyL>

                <DigitsInput
                    onChange={updateInput}
                    onKeyDown={onKeyDown}
                    label={translate('Last 4 Digits')}
                    labelId="verify-phone-number-check-last-digits-label"
                    describedBy="verify-phone-number-check-last-digits-label"
                    description={translate('Enter the last 4 digits of your phone number')}
                    numberOfDigits={4}
                    dataQa="verify-phone-number-check-last-digits-input"
                    dataFocus="verify-phone-number-check-last-digits-input"
                    autocomplete="last-phone-number-digits"
                    error={translate(textBoxError)}
                />
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Disclaimer>
                    <FontDetailS>
                        <Interpolate
                            text={translate(
                                "By selecting <BoldText>[[Placeholder for 'Send Now']]</BoldText> to receive a text message, you agree to receive a one-time passcode. Automated systems may be used. Message and data rates may apply."
                            )}
                            components={{
                                BoldText: () => <strong>{translate('Send Now')}</strong>
                            }}
                        />
                    </FontDetailS>
                </ContainerLayout.Footer.Disclaimer>
                <ContainerLayout.Footer.Buttons>
                    <ContainerLayout.Footer.Buttons.Primary
                        data-qa="verify-phone-number-check-primary-button"
                        text={translate('Send Now')}
                        onClick={sendSolution}
                        loading={isPrimaryButtonLoading}
                    />
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
        </ContainerLayout>
    );
};

export default PhoneNumberCheckPage;
